import { ContactData, FrictionlessMessageDataToSend } from './index';
import {
  CardVariablesFormat,
  Template,
  TemplateButtonType,
  TemplateComponentsArray,
} from '../../../../shared/typings/template';
import { ChatType } from '../components/chat-list-section/chat-row/typings';
import {
  FrequencyCappingState,
  RetryFrequencyOptions,
  StopRetryingAfterOptions,
} from '../../../../shared/typings/frequency-capping';
import ChatCache from '../states/cache';
import { CallDirection, CallStatusFromBE } from '../../../call-manager/typings';
import { Status } from './chat';

export enum MessageTypesEnum {
  TEXT = 'text',
  DOCUMENT = 'document',
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  CONTACTS = 'contacts',
  TEMPLATE = 'template',
  SYSTEM = 'system',
  STICKER = 'sticker',
  INTERACTIVE = 'interactive',
  BUTTON = 'button',
  FLOW_RESPONSE = 'flow_response',
  COMMERCE_PRIVACY_IMAGE = 'commerce_privacy_image',
  COMMERCE_PRIVACY_BUTTON = 'commerce_privacy_button',
  LOCATION = 'location',
  REACTION = 'reaction',
  SYSTEM_LOG = 'system_message_v2',
  NOTE = 'note',
  CALL = 'call',
  REMINDER = 'reminder',
  REMINDER_TRIGGER = 'reminder-trigger',
  UNSUPPORTED = 'unsupported',
}

export enum CommercePrivacyStatusEnum {
  ALLOWED = 'ALLOWED',
  REJECTED = 'REJECTED',
}

export enum FileTypes {
  IMAGE_JPEG = 'image/jpeg',
  IMAGE_PNG = 'image/png',
  PDF = 'application/pdf',
  EXCEL = 'application/vnd.ms-excel',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  VIDEO_MP4 = 'video/mp4',
  DOC = 'application/msword',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  VIDEO_3GP = 'video/3gpp',
  AUDIO_OGG = 'audio/ogg; codecs=opus',
  AUDIO_MPEG = 'audio/mpeg',
  AUDIO_MPGA = 'audio/mpga',
}

export enum TemplateDataKeys {
  HEADER = 'header',
  BODY = 'body',
  FOOTER = 'footer',
  BUTTON = 'button',
  CARDS = 'cards',
}

export type TemplateHeaderType = {
  data?: Array<
    | {
        type: MessageTypesEnum.TEXT;
        text: string;
      }
    | {
        type: MessageTypesEnum.IMAGE;
        link: string;
      }
    | {
        type: MessageTypesEnum.VIDEO;
        link: string;
      }
    | {
        type: MessageTypesEnum.DOCUMENT;
        link: string;
      }
  >;
};

export type TemplateHeaderItemTypeForCard =
  | {
      type: MessageTypesEnum.IMAGE;
      link: string;
    }
  | {
      type: MessageTypesEnum.VIDEO;
      link: string;
    }
  | {
      type: MessageTypesEnum.DOCUMENT;
      link: string;
    };

export type TemplateHeaderTypeForCard = {
  data?: Array<TemplateHeaderItemTypeForCard>;
};
export type TemplateButtonDataType = {
  data: Array<{
    type: TemplateButtonType;
    text: string;
    url?: string;
    phoneNumber?: string;
    example?: string;
  }>;
};
export type TemplateBodyType = {
  data: Array<{
    type: MessageTypesEnum.TEXT;
    text: string;
  }>;
};
export type TemplateCardDataType = {
  body: TemplateBodyType;
  header: TemplateHeaderTypeForCard;
  button?: TemplateButtonDataType;
  mode?: 'message' | 'preview';
  time?: string | number;
  stats?: ChatStats;
  cardIndex?: number;
  previewData?: CardVariablesFormat[];
  components?: TemplateComponentsArray;
  messageOriginType?: MessageOrigin;
  mediaViewerHandler?: () => void;
  onHandleMenuItemClick?: (pageDetail: string, buttonText: string, linkId: string, cardIndex?: number) => void;
};
export interface TemplateData {
  [TemplateDataKeys.HEADER]: TemplateHeaderType;
  [TemplateDataKeys.BUTTON]: TemplateButtonDataType;
  [TemplateDataKeys.CARDS]?: TemplateCardDataType[];
  [TemplateDataKeys.BODY]: TemplateBodyType;

  [TemplateDataKeys.FOOTER]: {
    data?: Array<{
      type: MessageTypesEnum.TEXT;
      text: string;
    }>;
  };
  hasCallPermissionRequest?: boolean;
}

export type TextMessage =
  | {
      text: string;
      isSlaActive?: boolean;
      slaDueDate?: string;
      messageType: MessageTypesEnum.TEXT;
      messageTypeWeb?: undefined;
      mediaDisable?: boolean;
      textDisable?: boolean;
      showReminderSuggestion?: boolean;
    }
  | {
      text: string;
      isSlaActive?: boolean;
      slaDueDate?: string;
      messageType: MessageTypesEnum.TEXT;
      messageTypeWeb: MessageTypesEnum.FLOW_RESPONSE;
      userResponse: {
        payload: FlowResponsePayload;
      };
      mediaDisable?: boolean;
      textDisable?: boolean;
      showReminderSuggestion?: boolean;
    };

export interface ReactionMessage {
  reactionMessage: {
    replyMessageId: string;
    emoji: string;
  };
  messageType: MessageTypesEnum.REACTION;
}

export interface ContactsMessage {
  contactsData: ContactData[];
  messageType: MessageTypesEnum.CONTACTS;
}

export interface AudioMessage {
  fileUrl: string;
  fileName: string;
  messageType: MessageTypesEnum.AUDIO;
  duration?: number;
  size: number;
}

export interface VideoMessage {
  size?: number;
  fileUrl: string;
  duration?: number;
  fileName: string;
  text?: string;
  width?: number;
  height?: number;
  thumbnailUrl: string;
  messageType: MessageTypesEnum.VIDEO;
  fileType: string;
}

export type SystemMessageLink = {
  type: SystemMessageLinkType;
  start: number;
  end: number;
  id: string;
};

export enum ActionType {
  CALL_PERMISSION_REJECTED = 'CALL_PERMISSION_REJECTED',
  CALL_PERMISSION_ACCEPTED = 'CALL_PERMISSION_ACCEPTED',
  CALL_PERMISSION_ACCEPTED_PERMANENT = 'CALL_PERMISSION_ACCEPTED_PERMANENT',
}

export enum ActorType {
  CUSTOMER = 'CUSTOMER',
  USER = 'CUSTOMER',
}

export interface SystemMessage {
  messageType: MessageTypesEnum.SYSTEM;
  text: string;
  actionType?: ActionType;
  actorType?: ActorType;
  actorId?: string;
  isSlaActive?: boolean;
  slaDueDate?: string;
  reaction?: {
    text: string;
  };
  botRunId?: string;
  botRunLogId?: string;
  links?: SystemMessageLink[];
}

export enum SystemMessageLinkType {
  BOT = 'BOT',
  TEMPLATE = 'TEMPLATE',
}

export type LogSegments = {
  type: 'TEXT' | SystemMessageLinkType;
  text: string;
  id?: string;
};

export type SystemLogMessage = {
  messageType: MessageTypesEnum.SYSTEM_LOG;
  text: string;
  reaction?: {
    text: string;
  };
  botRunId?: string;
  botRunLogId?: string;
  links?: SystemMessageLink[];

  isStartOfConversation?: boolean; // This flaf is only for FE use to indicate start of chat in WA Groups
};

export interface ImageMessage {
  size?: number;
  width?: number;
  height?: number;
  fileUrl: string;
  text?: string;
  fileType?: string;
  messageType: MessageTypesEnum.IMAGE;
  thumbnailUrl?: string;
  fileName?: string;
}

export interface StickerMessage {
  size?: number;
  width?: number;
  height?: number;
  fileUrl: string;
  text?: string;
  fileType?: string;
  messageType: MessageTypesEnum.STICKER;
  thumbnailUrl?: string;
  fileName?: string;
}

export interface DocumentMessage {
  text?: string;
  size?: number;
  fileUrl: string;
  pageCount?: number;
  fileName: string;
  fileType: string;
  messageType: MessageTypesEnum.DOCUMENT;
  thumbnailUrl?: string;
}

export interface TemplateMessage {
  messageType: MessageTypesEnum.TEMPLATE;
  templateMessage: TemplateData;
  payload?: string;
}

export type FlowMediaItem = {
  id: string | number;
  url: string;
  file_name: string;
  sizeInBytes?: number;
  mime_type: string;
};

interface FlowResponsePayloadValue {
  [key: string]: string | number | boolean;
}
export type FlowResponsePayload = {
  [key: string]: string | number | boolean | FlowResponsePayloadValue | Array<FlowResponsePayloadValue>;
};

export interface FlowResponseMessage {
  messageType: MessageTypesEnum.FLOW_RESPONSE;
  userResponse: {
    payload: FlowResponsePayload;
  };
}

export interface LocationMessage {
  latitude: string;
  longitude: string;
  messageType: MessageTypesEnum.LOCATION;
}

export enum CALL_SOURCE {
  EXOTEL_CALL = 'EXOTEL_CALL',
  INTERNAL_CALL = 'INTERNAL_CALL',
  CALL_PULSE = 'CALL_PULSE',
  PSTN = 'PSTN',
}

export enum CallSummaryStatus {
  TRANSCRIPT_ENQUEUED = 'transcription_enqueued',
  TRANSCRIPT_IN_PROCESS = 'transcription_in_progress',
  TRANSCRIPT_COMPLETED = 'transcription_completed',
  TRANSCRIPT_FAILED = 'transcription_failed',
  TRANSCRIPT_NOT_FOUND = 'transcription_not_found',
  SUMMARY_ENQUEUED = 'summary_enqueued',
  SUMMARY_IN_PROCESS = 'summary_in_progress',
  SUMMARY_COMPLETED = 'summary_completed',
  SUMMARY_FAILED = 'summary_failed',
  SUMMARY_NOT_POSSIBLE = 'summary_not_possible',
}

export interface CallTranscriptMessage {
  preview: string;
  status: CallSummaryStatus;
  errorMessage: string;
}

export interface CallSummarizeMessage {
  quickRecap: string;
  errorMessage: string;
  status: CallSummaryStatus;
}

export type CallMessage = {
  messageType: MessageTypesEnum.CALL;
  callId: string;
  callSource?: CALL_SOURCE;
  direction?: CallDirection;
  callStatus: CallStatusFromBE;
  durationInSeconds?: number;
  recordingUrl?: string | null;
  transcript?: string | null;
  transcription?: CallTranscriptMessage | null;
  summary?: CallSummarizeMessage | null;
  userId?: string | null;
};

export interface UnsupportedMessage {
  messageType: MessageTypesEnum.UNSUPPORTED;
  text?: string;
}

export interface PrivacyPayload {
  state: string;
  catalogueId: string;
  catalogueName: string;
  alreadyApproved: boolean;
  alreadyRejected: boolean;
  rejectButtonText: string;
  approveButtonText: string;
}

export interface CommercePrivacyImage {
  size: number;
  text: string;
  width: number;
  height: number;
  fileUrl: string;
  fileType: string;
  messageType: MessageTypesEnum.COMMERCE_PRIVACY_IMAGE;
  thumbnailUrl: string;
  privacyPayload: PrivacyPayload;
}

export interface CommercePrivacyButton {
  text: string;
  messageType: MessageTypesEnum.COMMERCE_PRIVACY_BUTTON;
  privacyPayload: PrivacyPayload;
}
export interface TemplateButtonAction {
  text: string;
  messageType: MessageTypesEnum.BUTTON;
  payload: string;
}

export interface CommercePrivacyAccessChat {
  chatId: string;
  chatType: ChatType;
  chatTypeId: string;
  assignedUserId: string;
  assignedUserName: string;
  name: string;
  phoneNumber: string;
  imageUrl: string;
  unreadCount: number;
  senderUser?: any;
}

export interface CommercePrivacyAccessData {
  chatId: string;
  messageId: string;
  message: CommercePrivacyImage;
  chat: CommercePrivacyAccessChat;
  messageTime: number;
  status: string;
  statusTimestamp: Date;
  senderId: string;
  senderType: MessageOrigin;
  orgId: string;
}
interface Reaction {
  reaction?: {
    text: string;
    messageType?: MessageTypesEnum.REACTION;
  };
  agentReaction?: {
    text: string;
    messageType: MessageTypesEnum.REACTION;
    agentId: string;
    agentName: string;
    agentPhone?: string;
  };
}

interface ContentRejected {
  contentRejected?: boolean;
}

type BaseMessageTypes =
  | TextMessage
  | AudioMessage
  | VideoMessage
  | DocumentMessage
  | ImageMessage
  | ContactsMessage
  | TemplateMessage
  | FlowResponseMessage
  | StickerMessage
  | SystemMessage
  | InteractiveMessage
  | CommercePrivacyImage
  | CommercePrivacyButton
  | TemplateButtonAction
  | LocationMessage
  | CallMessage
  | ReactionMessage
  | SystemLogMessage
  | NoteMessage
  | ReminderMessage
  | ReminderTriggerMessage
  | UnsupportedMessage;

type MessageTypesWithReaction<T> = T & Reaction;

export type MessageTypes = MessageTypesWithReaction<BaseMessageTypes> & ContentRejected;

export enum MessageOrigin {
  CUSTOMER = 'CUSTOMER',
  USER = 'USER',
  SYSTEM = 'SYSTEM',
}

export enum MessageMetadataStatus {
  PROCESSING = 'PROCESSING',
  CAN_ADD_TO_ORDER = 'CAN_ADD_TO_ORDER',
  ADDED_TO_CART = 'ADDED_TO_CART',
}

export interface MessageMetadata {
  status: MessageMetadataStatus;
}

export interface OrderData {
  itemTotal: number;
  currencySymbol: string;
  orderTotal: number;
  viewLink: string;
}

export interface BroadcastFrequencyCappingSettings {
  id?: string;
  retryImmediately?: boolean;
  endDate?: string;
  frequency?: RetryFrequencyOptions;
  stopRetryingAfter?: StopRetryingAfterOptions;
  customTime?: string;
  shouldSendRetryOutsideWorkingHours?: boolean;
  timeZone?: string;
  lastExecutedAt?: string;
  nextScheduledAt?: string;
  retryFailedMessages?: boolean; //only for FE use,not coming from BE
  state?: FrequencyCappingState;
}

export enum SenderProvider {
  AI_BOT = 'bot',
  HUMAN = 'human',
  BOT_AUTOMATION = 'bot_automation',
}

export type BaseSenderBotUser = {
  botId?: string;
  botName?: string;
  runId?: string;
};

export type BaseSenderUser = {
  id: string | null;
  name: string | null;
  phoneNumber?: string;
  imageUrl: string | null;
  provider?: SenderProvider;
  isFromPublicApi?: boolean;
  senderBot?: BaseSenderBotUser;
};

export type SimpleMediaMessageForMediaViewer = {
  filesUrl: string[];
  messageTypes: MessageTypesEnum[];
  forwardPayloads?: {
    msg: MessageTypes;
    msgId: string;
  }[];
  height?: number;
  width?: number;
  text?: string;
  fileName?: string;
  activeIndex?: number;
};

export type Message = {
  id: string;
  chatId?: string;
  message: MessageTypes;
  templateId?: string | null;
  senderId: string;
  messageOriginType: MessageOrigin;
  readCount: ChatStats['readCount'];
  replyMessage?: Message;
  repliesCount: ChatStats['repliesCount'];
  deliveryCount: ChatStats['deliveryCount'];
  erroredCount: ChatStats['erroredCount'];
  sentCount: ChatStats['sentCount'];
  senderUser?: BaseSenderUser;
  messageTime: number;
  messageMetadata: MessageMetadata | null;
  totalCount: ChatStats['totalCount'];
  errorMessage: string;
  quickActionButtonStats?: ChatStats['quickActionButtonStats'];
  linkAnalytics?: ChatStats['linkAnalytics'];
  errorCountFrequencyCapping?: ChatStats['errorCountFrequencyCapping'];
  cardLinkAnalytics?: Array<CardLinkAnalytics>;
  adReferralData?: AdReferralData;
  unreadCount: ChatStats['unreadCount'];
  noResponseCount: ChatStats['noResponseCount'];
  broadcastFrequencyCappingSettings?: BroadcastFrequencyCappingSettings;
  awaitingTemplateApprovalCount: ChatStats['awaitingTemplateApprovalCount'];
  canRetry?: boolean;
  contentRejected?: boolean;
  retry?: boolean;
  status?: Status;
  groupId?: string;
  retryFunction?: () => Promise<unknown>;
  groupName?: string;
  groupViewChatId?: string;
  showReadMore?: boolean;
  isRetarget?: boolean;
  retargetAction?: string | null;
  memberMessageStatuses?: MemberMessageStatus[];
};

export interface MemberMessageStatus {
  customerId: string;
  customerName: string;
  customerPhoneNumber: string;
  sentAt: string | null;
  deliveredAt: string | null;
  readAt: string | null;
  failedAt: string | null;
  failedReason: string | null;
  errorCode: string | null;
}

export type AdReferralData = {
  sourceUrl: string;
  sourceId: string;
  sourceType: string;
  body: string;
  headline: string;
} & (
  | {
      mediaType: 'image';
      imageUrl: string;
    }
  | {
      mediaType: 'video';
      videoUrl: string;
      thumbnailUrl: string;
    }
);

export enum ScheduledMessageStatus {
  ENQUEUED = 'ENQUEUED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  DELETED = 'DELETED',
}

export interface EnqueuedMessage {
  status: ScheduledMessageStatus;
  chatMessageId: string;
  eventTimestamp: number;
  messageType: ChatType;
}
export interface LinkAnalyticsType {
  actualURL: string;
  linkVisitCount: number;
  linkId: string;
}
export interface CardLinkAnalytics {
  cardIndex: number;
  linkAnalytics: Array<LinkAnalyticsType>;
}
export interface ChatStats {
  repliesCount: number;
  deliveryCount: number;
  erroredCount: number;
  errorCountFrequencyCapping?: number;
  readCount: number;
  sentCount: number;
  totalCount: number;
  cardButtonStats?: Array<{
    index: number;
    buttons: Array<{ text: string; count: number }>;
  }>;
  cardLinkAnalytics?: Array<CardLinkAnalytics>;
  quickActionButtonStats?: Array<{ text: string; count: number }>;
  linkAnalytics?: Array<{
    linkId: string;
    actualURL: string;
    linkVisitCount: number;
  }>;
  unreadCount: number;
  noResponseCount: number;
  awaitingTemplateApprovalCount: number;
}

export interface SendMessage {
  message: MessageTypes | TemplateSendMessage;
  chatId: string;
  phoneNumber: string;
  chatMessageId: string;
  messageTimestamp: number;
  scheduledTimestamp?: number;
  replyMessageId?: string;
}

export interface SendClosedConversationTemplate {
  message: FrictionlessMessageDataToSend;
  chatId: string;
  phoneNumber: string;
  integrationId: string;
  chatMessageId: string;
  messageTimestamp: number;
  scheduledTimestamp?: number;
  replyMessageId?: string;
}

interface ComponentParameter {
  type: string;
  link?: string; // Optional for image type
  text?: string; // Optional for text type
}

interface Component {
  type: string;
  parameters: ComponentParameter[];
}

interface Card {
  cardIndex: number;
  components: Component[];
}

interface CardVariable {
  cardVariables: Card[];
}
export interface SendBroadcastMessage {
  message: TemplateSendMessage;
  groupId: string;
  integrationId: string;
  scheduledTimestamp?: number;
  messageTimestamp: number;
  broadcastFrequencyCappingSettings?: FrequencyCappingSettings;
  sendFromLastCommunicatedIntegration?: boolean;
}

export interface FrequencyCappingSettings {
  frequency: RetryFrequencyOptions;
  stopRetryingAfter: StopRetryingAfterOptions;
  shouldSendRetryOutsideWorkingHours?: boolean;
  customTime?: string;
  timeZone?: string;
  endDate?: string;
}

export interface TemplateSendMessage {
  messageType: MessageTypesEnum;
  templateMessage: {
    id: number;
    cardVariables?: CardVariablesFormat[];
    variables?: Array<{
      type: string;
      text?: string;
      link?: string;
      fileName?: string;
    }>;
  };
}

export interface SendTemplateMessage {
  message: TemplateSendMessage;
  chatId: string;
  chatMessageId: string;
  customerId?: string;
  groupId?: string;
  messageTimestamp: number;
  broadcastFrequencyCappingSettings?: FrequencyCappingSettings;
}

export interface MessageInfo {
  details: {
    deliveredAt?: number;
    readAt?: number;
    failedAt?: number;
    failedReason?: string;
    template?: Template;
  } | null;
}

export interface InteractiveMessage {
  messageType: MessageTypesEnum.INTERACTIVE;
  interactiveMessage:
    | InteractiveListMessage
    | InteractiveButtonMessage
    | InteractiveCTAButtonMessage
    | InteractiveCTAFlowMessage
    | InteractiveMediaCarouselMessage;
}

export enum InteractiveMessageTypes {
  BUTTON = 'button',
  LIST = 'list',
  CTA_URL = 'cta_url',
  FLOW = 'flow',
  CAROUSEL = 'carousel',
}

// Interactive media carousel — Meta's multi-card carousel of image/video
// cards with URL or quick-reply buttons. The cards are persisted in the
// same shape as template-carousel cards (TemplateCardDataType) so the
// existing TemplateCardRenderer can be reused for display.
export interface InteractiveMediaCarouselMessage {
  type: InteractiveMessageTypes.CAROUSEL;
  body: {
    text: string;
  };
  cards: TemplateCardDataType[];
}

export interface InteractiveMessageButton {
  id: string;
  title: string;
}

export interface InteractiveListMessage {
  body: {
    text: string;
  };
  type: InteractiveMessageTypes.LIST;
  button: string;
  header: Omit<TextMessage, 'messageType'> & {
    type: MessageTypesEnum.TEXT;
  };
  footer?: {
    text: string;
  };
  sections: Array<{
    title?: string;
    rows: Array<{ id: string; title: string }>;
  }>;
}

export interface InteractiveButtonMessage {
  body: {
    text: string;
  };
  footer?: {
    text: string;
  };
  type: InteractiveMessageTypes.BUTTON;
  buttons: InteractiveMessageButton[];
  header?:
    | (Omit<ImageMessage, 'messageType' | 'text'> & {
        type: MessageTypesEnum.IMAGE;
      })
    | (Omit<VideoMessage, 'messageType' | 'text'> & {
        type: MessageTypesEnum.VIDEO;
      })
    | (Omit<DocumentMessage, 'messageType' | 'text'> & {
        type: MessageTypesEnum.DOCUMENT;
      })
    | (Omit<TextMessage, 'messageType'> & {
        type: MessageTypesEnum.TEXT;
      });
}

export interface InteractiveCTAButtonMessage {
  body: {
    text: string;
  };
  type: InteractiveMessageTypes.CTA_URL;
  header?:
    | (Omit<ImageMessage, 'messageType' | 'text'> & {
        type: MessageTypesEnum.IMAGE;
      })
    | (Omit<VideoMessage, 'messageType' | 'text'> & {
        type: MessageTypesEnum.VIDEO;
      })
    | (Omit<DocumentMessage, 'messageType' | 'text'> & {
        type: MessageTypesEnum.DOCUMENT;
      })
    | (Omit<TextMessage, 'messageType'> & {
        type: MessageTypesEnum.TEXT;
      });
  footer?: {
    text: string;
  };
  parameters: {
    display_text: string;
    url: string;
  };
}

export interface InteractiveCTAFlowMessage {
  body: {
    text: string;
  };
  type: InteractiveMessageTypes.FLOW;
  header?:
    | (Omit<ImageMessage, 'messageType' | 'text'> & {
        type: MessageTypesEnum.IMAGE;
      })
    | (Omit<VideoMessage, 'messageType' | 'text'> & {
        type: MessageTypesEnum.VIDEO;
      })
    | (Omit<DocumentMessage, 'messageType' | 'text'> & {
        type: MessageTypesEnum.DOCUMENT;
      })
    | (Omit<TextMessage, 'messageType'> & {
        type: MessageTypesEnum.TEXT;
      });
  footer?: {
    text: string;
  };
  parameters: {
    flow_id: string;
    flow_cta: string;
  };
}

export interface ButtonMessage {
  messageType: MessageTypesEnum.BUTTON;
  text: string;
  payload: string;
}

export interface ChatMessagesResult {
  data: string[] | null | undefined;
  loading: boolean | undefined;
  fetched: boolean | undefined;
  fetchMoreOlder: () => void | Promise<void>;
  fetchMoreNewer: () => void | Promise<void>;
  refresh: (args?: Record<string, unknown>) => void | Promise<void>;
  chatCache: ChatCache;
  hasMoreOlder: boolean;
  hasMoreNewer: boolean;
  isFetchingMore: boolean;
}

export interface NoteMessage {
  text: string;
  messageType: MessageTypesEnum.NOTE;
}

export enum REMINDER_MESSAGE_STATUS {
  PENDING = 'PENDING',
  TRIGGERED = 'TRIGGERED',
  DISMISSED = 'DISMISSED',
}

export interface ReminderMessage {
  reminderMessage: string;
  assigneeDetails: { userId: string; name: string }[];
  dueAt: string;
  reminderStatus: REMINDER_MESSAGE_STATUS;
  messageType: MessageTypesEnum.REMINDER;
  reminderId: string;
  createdBy: string;
  hideMarkAsDoneIconAt?: number;
}

export interface ReminderTriggerMessage {
  reminderMessage: string;
  assigneeDetails: { userId: string; name: string }[];
  dueAt: string;
  reminderStatus: REMINDER_MESSAGE_STATUS;
  messageType: MessageTypesEnum.REMINDER_TRIGGER;
  reminderId: string;
  createdBy: string;
  hideMarkAsDoneIconAt?: number;
}
